import type {
  ServiceGetAllResponse,
  ServiceGetByIdResponse,
  ServiceMutationOptionType,
  ServiceQueryOptionType
} from '@/types/service'

import { api, handleAxiosError } from '@/lib/axios'

import type { FilterSubitemKepValues } from '../validation'
import type { KegiatanUsahaDataType } from './types'
import type { MutationKegiatanUsahaValues } from './validation'

const QUERY_KEY = (key?: string | Record<string, any>) =>
  ['kep-kegiatan-usaha', key] as const

const getAll = (
  params?: FilterSubitemKepValues
): ServiceQueryOptionType<
  ServiceGetAllResponse<Array<KegiatanUsahaDataType>>
> => ({
  queryKey: QUERY_KEY(params),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<KegiatanUsahaDataType>>
      >(`/kelembagaan/kep/${params?.id_kep}/kegiatan-usaha`, {
        params: {
          ...params,
          id_kep: undefined
        }
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const create = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<KegiatanUsahaDataType>,
  MutationKegiatanUsahaValues
> => ({
  mutationFn: async (data: MutationKegiatanUsahaValues) => {
    try {
      const response = await api.post<
        ServiceGetByIdResponse<KegiatanUsahaDataType>
      >(`/kelembagaan/kep/${data?.id_kep}/kegiatan-usaha`, {
        ...data,
        id_kep: undefined,
        id_subsektor: data?.id_subsektor?.value,
        is_export: data?.is_export === 'y',
        is_regional: data?.is_regional === 'y',
        luas_lahan: data?.luas_lahan
          ? Number.parseFloat(data?.luas_lahan)
          : undefined
      })
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const update = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<KegiatanUsahaDataType>,
  MutationKegiatanUsahaValues
> => ({
  mutationFn: async (data: MutationKegiatanUsahaValues) => {
    try {
      const response = await api.put<
        ServiceGetByIdResponse<KegiatanUsahaDataType>
      >(
        `/kelembagaan/kep/${data?.id_kep}/kegiatan-usaha/${data.id_kegiatan_usaha}`,
        {
          ...data,
          id_kep: undefined,
          id_subsektor: data?.id_subsektor?.value,
          is_export: data?.is_export === 'y',
          is_regional: data?.is_regional === 'y',
          luas_lahan: data?.luas_lahan
            ? Number.parseFloat(data?.luas_lahan)
            : undefined
        }
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const deleteData = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<KegiatanUsahaDataType>,
  { id_kep: string; id_data: string }
> => ({
  mutationFn: async (data: { id_kep: string; id_data: string }) => {
    try {
      const response = await api.delete<
        ServiceGetByIdResponse<KegiatanUsahaDataType>
      >(`/kelembagaan/kep/${data?.id_kep}/kegiatan-usaha/${data?.id_data}`)
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

export const kegiatanUsaha = {
  queryKey: [QUERY_KEY(undefined)[0]],
  getAll,
  create,
  update,
  deleteData
}
